if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.currentColor = null;
        this.isSceneSlider = this.dataset.sceneSliderEnabled === 'true';

        if (this.elements.thumbnails) {
          this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          });
          if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        }

        this.initSceneSlider();
        
        // Initialize color filtering if enabled
        this.initColorFiltering();
      }

      refreshThumbnailListeners() {
        this.elements.thumbnails = this.querySelector('[id^="GalleryThumbnails"]');
        if (!this.elements.thumbnails) return;

        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          const button = mediaToSwitch.querySelector('button');
          if (button) {
            button.addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          }
        });
      }

      initSceneSlider() {
        if (!this.isSceneSlider) return;

        this.sceneItems = Array.from(this.elements.viewer.querySelectorAll('[data-scene-color]'));
        this.sceneSlider = this.elements.viewer.querySelector('[id^="Slider-Gallery"]');
        this.sceneNav = this.querySelector('.media-gallery__color-nav');
        this.scenePrevButton = this.querySelector('.media-gallery__color-nav-button--prev');
        this.sceneNextButton = this.querySelector('.media-gallery__color-nav-button--next');
        if (!this.sceneItems.length || !this.sceneSlider) return;
        this.scenePointerStartX = null;
        this.scenePointerStartY = null;
        this.sceneWasDragged = false;

        this.scenePrevButton?.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.navigateScene(-1);
        });
        this.sceneNextButton?.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.navigateScene(1);
        });

        this.supportsSceneScrollEnd = 'onscrollend' in this.sceneSlider;
        this.sceneSlider.addEventListener('scroll', () => {
          if (this.supportsSceneScrollEnd) return;
          window.clearTimeout(this.sceneScrollSettleTimer);
          this.sceneScrollSettleTimer = window.setTimeout(() => this.handleSceneScrollEnd(), 240);
        });
        if (this.supportsSceneScrollEnd) {
          this.sceneSlider.addEventListener('scrollend', () => this.handleSceneScrollEnd());
        }

        this.sceneSlider.addEventListener('pointerdown', (event) => {
          this.releaseProgrammaticSceneScroll();
          this.releasePendingSceneVariant();
          this.scenePointerStartX = event.clientX;
          this.scenePointerStartY = event.clientY;
          this.scenePointerStartScrollLeft = this.sceneSlider.scrollLeft;
          this.sceneWasDragged = false;
        });
        this.sceneSlider.addEventListener('pointermove', (event) => {
          if (this.scenePointerStartX === null) return;
          const horizontalDelta = event.clientX - this.scenePointerStartX;
          const horizontalDistance = Math.abs(event.clientX - this.scenePointerStartX);
          const verticalDistance = Math.abs(event.clientY - this.scenePointerStartY);
          if (horizontalDistance > 8 && horizontalDistance > verticalDistance) {
            this.sceneWasDragged = true;
            if (!this.mql.matches) {
              event.preventDefault();
              this.sceneSlider.scrollLeft = this.scenePointerStartScrollLeft - horizontalDelta;
            }
          }
        });
        this.sceneSlider.addEventListener('pointerup', (event) => {
          const horizontalDelta =
            this.scenePointerStartX === null ? 0 : event.clientX - this.scenePointerStartX;
          const verticalDelta =
            this.scenePointerStartY === null ? 0 : event.clientY - this.scenePointerStartY;
          if (this.sceneWasDragged) this.sceneSuppressClickUntil = performance.now() + 600;
          if (!this.mql.matches && Math.abs(horizontalDelta) > Math.abs(verticalDelta)) {
            this.finishSceneSwipe(horizontalDelta);
          }
          this.scenePointerStartX = null;
          this.scenePointerStartY = null;
        });
        this.sceneSlider.addEventListener('pointercancel', () => {
          if (this.sceneWasDragged) this.sceneSuppressClickUntil = performance.now() + 600;
          this.scenePointerStartX = null;
          this.scenePointerStartY = null;
        });
        this.sceneSlider.addEventListener(
          'click',
          (event) => {
            if (
              event.target.closest('.product-scene-link') &&
              performance.now() < (this.sceneSuppressClickUntil || 0)
            ) {
              event.preventDefault();
              event.stopPropagation();
            }
          },
          true
        );
      }

      finishSceneSwipe(horizontalDelta) {
        const currentIndex = this.getCurrentSceneIndex();
        const crossedThreshold = Math.abs(horizontalDelta) >= 24;
        const direction = horizontalDelta < 0 ? 1 : -1;
        const targetIndex = crossedThreshold
          ? Math.min(Math.max(currentIndex + direction, 0), this.sceneItems.length - 1)
          : currentIndex;
        const targetScene = this.sceneItems[targetIndex];

        this.activateScene(targetScene, targetIndex !== currentIndex);
      }

      navigateScene(direction) {
        if (!this.sceneItems?.length) return;
        const currentIndex = this.getCurrentSceneIndex();
        const targetIndex = Math.min(Math.max(currentIndex + direction, 0), this.sceneItems.length - 1);
        if (targetIndex === currentIndex) return;
        this.activateScene(this.sceneItems[targetIndex], true);
      }

      handleSceneScroll() {
        if (
          !this.isSceneSlider ||
          !this.sceneItems?.length ||
          this.scenePointerStartX !== null ||
          this.sceneProgrammaticScroll ||
          this.sceneVariantPending
        ) {
          return;
        }
        const visibleScene = this.sceneItems.reduce((closest, item) =>
          Math.abs(item.offsetLeft - this.sceneSlider.scrollLeft) <
          Math.abs(closest.offsetLeft - this.sceneSlider.scrollLeft)
            ? item
            : closest
        );
        this.activateScene(visibleScene, true, false);
      }

      handleSceneScrollEnd() {
        window.clearTimeout(this.sceneScrollSettleTimer);
        const wasProgrammatic = this.sceneProgrammaticScroll;
        this.releaseProgrammaticSceneScroll();
        if (!wasProgrammatic && !this.sceneVariantPending) this.handleSceneScroll();
      }

      getCurrentSceneIndex() {
        const colorIndex = this.sceneItems.findIndex(
          (item) => this.normalizeColor(item.dataset.sceneColor) === this.currentColor
        );
        if (colorIndex >= 0) return colorIndex;

        const activeIndex = this.sceneItems.findIndex((item) => item.classList.contains('is-active'));
        return activeIndex >= 0 ? activeIndex : 0;
      }

      activateScene(sceneItem, syncVariant = false, shouldScroll = true) {
        if (!sceneItem) return;

        this.sceneItems.forEach((item) => item.classList.toggle('is-active', item === sceneItem));
        if (shouldScroll) {
          this.sceneProgrammaticScroll = true;
          window.clearTimeout(this.sceneScrollUnlockTimer);
          this.sceneSlider.scrollTo({ left: sceneItem.offsetLeft, behavior: 'smooth' });
          this.sceneScrollUnlockTimer = window.setTimeout(
            () => this.releaseProgrammaticSceneScroll(),
            700
          );
        }

        const sceneIndex = this.sceneItems.indexOf(sceneItem);
        this.updateSceneButtons(sceneIndex);
        if (syncVariant) this.syncVariantToScene(sceneItem.dataset.sceneColor);
      }

      releaseProgrammaticSceneScroll() {
        window.clearTimeout(this.sceneScrollUnlockTimer);
        this.sceneProgrammaticScroll = false;
      }

      releasePendingSceneVariant() {
        window.clearTimeout(this.sceneVariantUnlockTimer);
        this.sceneVariantPending = false;
      }

      updateSceneButtons(sceneIndex) {
        if (this.scenePrevButton) this.scenePrevButton.disabled = sceneIndex <= 0;
        if (this.sceneNextButton) this.sceneNextButton.disabled = sceneIndex >= this.sceneItems.length - 1;
      }

      syncVariantToScene(color) {
        const normalizedColor = this.normalizeColor(color);
        if (!normalizedColor || normalizedColor === this.currentColor) return;

        const variantSelects = this.closest('product-info')?.querySelector('variant-selects');
        if (!variantSelects) return;

        const colorPosition = Number.parseInt(this.dataset.colorOptionPosition, 10);
        const colorField = Array.from(variantSelects.querySelectorAll('.product-form__input'))[
          colorPosition - 1
        ];
        const radio = Array.from(colorField?.querySelectorAll('input[type="radio"]') || []).find(
          (input) => this.normalizeColor(input.value) === normalizedColor
        );
        const select = colorField?.querySelector('select');
        const selectOption = Array.from(select?.options || []).find(
          (option) => this.normalizeColor(option.value) === normalizedColor
        );

        if (radio) {
          this.currentColor = normalizedColor;
          this.sceneVariantPending = true;
          window.clearTimeout(this.sceneVariantUnlockTimer);
          this.sceneVariantUnlockTimer = window.setTimeout(
            () => this.releasePendingSceneVariant(),
            2000
          );
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        } else if (select && selectOption) {
          this.currentColor = normalizedColor;
          this.sceneVariantPending = true;
          window.clearTimeout(this.sceneVariantUnlockTimer);
          this.sceneVariantUnlockTimer = window.setTimeout(
            () => this.releasePendingSceneVariant(),
            2000
          );
          select.value = selectOption.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      updateSceneLinks(variantId) {
        if (!this.isSceneSlider || !variantId || !this.dataset.productUrl) return;
        const separator = this.dataset.productUrl.includes('?') ? '&' : '?';
        const href = `${this.dataset.productUrl}${separator}variant=${variantId}`;
        this.sceneItems?.forEach((item) => {
          item.querySelector('.product-scene-link')?.setAttribute('href', href);
        });
      }

      initColorFiltering() {
        // Scene images can switch independently of the original media filter.
        const colorSwitchingEnabled = this.dataset.enableColorSwitching === 'true';
        const hasSceneImages = this.dataset.hasSceneImages === 'true';
        if (!colorSwitchingEnabled && !hasSceneImages) {
          return;
        }

        // Listen for variant changes (only respond to this section's variant change when multiple sections exist)
        if (typeof PUB_SUB_EVENTS !== 'undefined') {
          subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            const mySectionId = this.closest('product-info')?.dataset?.section;
            if (event.data?.sectionId !== mySectionId) return;
            this.handleVariantChange(event.data);
          });
        }

        if (this.dataset.selectedColor) {
          this.currentColor = this.normalizeColor(this.dataset.selectedColor);
          this.filterMediaByColor(this.dataset.selectedColor, this.dataset.selectedMediaId);
        }
      }

      handleVariantChange(data) {
        // Extract color from variant
        const variant = data?.variant;
        if (!variant) return;

        let selectedColor = null;

        // Product option names are not included in every variant-change event.
        // Liquid provides the exact Color option position for this gallery.
        const colorOptionPosition = Number.parseInt(this.dataset.colorOptionPosition, 10);
        if (colorOptionPosition >= 1 && colorOptionPosition <= 3) {
          selectedColor = variant[`option${colorOptionPosition}`];
        }
        
        // First, try to find an option named "Color" (case-insensitive)
        const optionNames = data?.product?.options;
        if (!selectedColor && optionNames && Array.isArray(optionNames)) {
          for (let i = 0; i < optionNames.length; i++) {
            const optionName = optionNames[i];
            if (optionName && typeof optionName === 'string' && optionName.toLowerCase() === 'color') {
              selectedColor = variant[`option${i + 1}`];
              break;
            }
          }
        }
        
        // If no "Color" option found, check for color keywords
        if (!selectedColor) {
          const colorKeywords = ['red', 'blue', 'green', 'black', 'white', 'yellow', 'orange', 'purple', 'pink', 'gray', 'grey', 'brown', 'navy', 'beige', 'silver', 'gold', 'maroon', 'teal', 'coral', 'mint', 'lavender', 'rose', 'cream', 'charcoal', 'ivory', 'burgundy', 'turquoise', 'peach', 'lime', 'indigo', 'violet', 'magenta', 'cyan', 'amber', 'copper', 'bronze', 'platinum', 'twilight', 'sage', 'storm', 'sky', 'slate', 'stone', 'sand', 'earth', 'ocean', 'forest', 'sunset', 'dawn', 'dusk', 'midnight', 'clear'];
          
          for (let i = 1; i <= 3; i++) {
            const optionValue = variant[`option${i}`];
            if (optionValue) {
              const optionLower = optionValue.toLowerCase();
              // Check if this option value contains a color keyword
              if (colorKeywords.some(color => optionLower.includes(color))) {
                selectedColor = optionValue;
                break;
              }
            }
          }

          // Fallback: use first variant option when no keyword matches (e.g. Clear, Smoke, custom names)
          if (!selectedColor) {
            for (let i = 1; i <= 3; i++) {
              const optionValue = variant[`option${i}`];
              if (optionValue) {
                selectedColor = optionValue;
                break;
              }
            }
          }
        }

        const normalizedSelected = this.normalizeColor(selectedColor);
        if (normalizedSelected) {
          this.currentColor = normalizedSelected;
          this.updateSceneLinks(variant.id);
          const featuredMediaId = variant.featured_media?.id
            ? `${data.sectionId}-${variant.featured_media.id}`
            : null;
          this.filterMediaByColor(selectedColor, featuredMediaId);
          this.releasePendingSceneVariant();
        }
      }

      normalizeColor(color) {
        return (color || '').replace(/^#/, '').trim().toLowerCase();
      }

      filterMediaByColor(color, preferredMediaId = null) {
        if (!color) return;

        const colorLower = this.normalizeColor(color);
        
        const allMediaItems = this.elements.viewer.querySelectorAll('[data-media-id]');
        const matchingSceneMedia = Array.from(allMediaItems).find(
          (item) => this.normalizeColor(item.dataset.sceneColor) === colorLower
        );

        // A configured scene image replaces the gallery for its matching color.
        if (matchingSceneMedia) {
          if (this.isSceneSlider) {
            allMediaItems.forEach((item) => {
              if (item.dataset.sceneColor) {
                if (item.style.display === 'none') item.style.removeProperty('display');
              } else if (item.style.display !== 'none') {
                item.style.display = 'none';
              }
            });
            if (this.elements.thumbnails && this.elements.thumbnails.style.display !== 'none') {
              this.elements.thumbnails.style.display = 'none';
            }
            if (this.sceneNav?.hidden) this.sceneNav.hidden = false;
            this.activateScene(matchingSceneMedia);
            return;
          }

          allMediaItems.forEach((item) => {
            item.style.display = item === matchingSceneMedia ? '' : 'none';
          });

          if (this.elements.thumbnails) {
            this.elements.thumbnails.style.display = 'none';
          }

          this.setActiveMedia(matchingSceneMedia.dataset.mediaId, false);
          return;
        }

        if (this.sceneNav) {
          this.sceneNav.hidden = true;
        }
        if (this.elements.thumbnails) {
          this.elements.thumbnails.style.display = '';
        }

        const colorSwitchingEnabled = this.dataset.enableColorSwitching === 'true';
        if (!colorSwitchingEnabled) {
          allMediaItems.forEach((item) => {
            item.style.display = item.dataset.sceneColor ? 'none' : '';
          });
          if (this.elements.thumbnails) {
            this.elements.thumbnails.querySelectorAll('[data-target]').forEach((item) => {
              item.style.display = item.dataset.sceneColor ? 'none' : '';
            });
          }
          if (preferredMediaId) {
            this.setActiveMedia(preferredMediaId, false);
          } else if (this.elements.viewer.querySelector('.is-active')?.dataset.sceneColor) {
            const firstProductMedia = Array.from(allMediaItems).find((item) => !item.dataset.sceneColor);
            if (firstProductMedia) this.setActiveMedia(firstProductMedia.dataset.mediaId, false);
          }
          return;
        }

        // No matching scene image: preserve the original product-media
        // filtering behavior when that setting is enabled.
        let firstVisibleMedia = null;
        
        allMediaItems.forEach(item => {
          if (item.dataset.sceneColor) {
            item.style.display = 'none';
            return;
          }

          const itemColor = item.dataset.color;
          if (itemColor) {
            // Normalize item color: remove '#' prefix and trim
            const normalizedItemColor = this.normalizeColor(itemColor);
            if (normalizedItemColor === colorLower) {
              item.style.display = '';
              if (!firstVisibleMedia) {
                firstVisibleMedia = item;
              }
            } else {
              item.style.display = 'none';
            }
          } else {
            item.style.display = 'none';
          }
        });

        // Filter thumbnail items
        if (this.elements.thumbnails) {
          const allThumbnails = this.elements.thumbnails.querySelectorAll('[data-target]');
          allThumbnails.forEach(item => {
            if (item.dataset.sceneColor) {
              item.style.display = 'none';
              return;
            }

            const itemColor = item.dataset.color;
            if (itemColor) {
              // Normalize item color: remove '#' prefix and trim
              const normalizedItemColor = this.normalizeColor(itemColor);
              if (normalizedItemColor === colorLower) {
                item.style.display = '';
              } else {
                item.style.display = 'none';
              }
            } else {
              item.style.display = 'none';
            }
          });
        }

        // Activate the first visible media item
        if (firstVisibleMedia) {
          this.setActiveMedia(firstVisibleMedia.dataset.mediaId, false);
        }
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      ensureDeferredMediaVisible(activeItem) {
        if (!activeItem) return;

        // Some flows end up writing inline `display:none` onto <deferred-media>,
        // which overrides CSS and makes the active media appear blank.
        const deferred =
          activeItem.querySelector('deferred-media.deferred-media, deferred-media, .deferred-media');
        if (!deferred || !deferred.style) return;

        if (deferred.style.display === 'none') {
          deferred.style.display = '';
        } else if (deferred.style.getPropertyValue('display') === 'none') {
          deferred.style.removeProperty('display');
        }
      }

      setActiveMedia(mediaId, prepend) {
        const requestedMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        const currentSceneMedia = this.isSceneSlider
          ? this.sceneItems?.find(
              (item) => this.normalizeColor(item.dataset.sceneColor) === this.currentColor
            )
          : null;

        // ProductInfo activates variant featured media before publishing its
        // variant-change event. In scene mode that must not override the scene.
        if (currentSceneMedia && requestedMedia && !requestedMedia.dataset.sceneColor) return;

        const activeMedia = requestedMedia || this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }

        // If the requested media is hidden (filtered out), find first visible one
        if (activeMedia.style.display === 'none') {
          const firstVisible = Array.from(this.elements.viewer.querySelectorAll('[data-media-id]'))
            .find(item => item.style.display !== 'none');
          if (firstVisible) {
            this.setActiveMedia(firstVisible.dataset.mediaId, prepend);
            return;
          }
        }

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');
        this.ensureDeferredMediaVisible(activeMedia);

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail && activeThumbnail.parentElement.firstChild !== activeThumbnail) {
              activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        
        // Only set active thumbnail if it's visible (not filtered out)
        if (activeThumbnail && activeThumbnail.style.display !== 'none') {
          this.setActiveThumbnail(activeThumbnail);
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) {
          // Defensive: ensure inline display:none doesn't hide active video.
          this.ensureDeferredMediaVisible(activeItem);
          deferredMedia.loadContent(false);
        }
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
